import { useEffect, useRef, useState } from "react";

import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { ClickableTextPanelContainer } from "@/Common/Components/ClickableText/ClickableTextPanelContainer";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { PopulationBasedFundingCollapsibleEventDetails } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingCollapsibleEventDetails";
import { PopulationBasedFundingEventDetails } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingEventDetails";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PopulationBasedFundingHelper } from "@/Common/Helpers/PopulationBasedFundingHelper";
import { PopulationBasedFundingEventWithNotesModel } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingEventWithNotesModel";
import { PopulationBasedFundingEventAction, PopulationBasedFundingEventOverrideReason } from "@/Common/Models/UI/Enums";
import { PopulationBasedFundingPortfolioHelper } from "@/Portfolio//Helpers/PopulationBasedFundingPortfolioHelper";
import { PopulationBasedFundingPortfolioActions } from "@/Portfolio/Services/PopulationBasedFundingPortfolioService";

import styles from "./PopulationBasedFundingPortfolioOverridePopup.module.scss";

interface PopulationBasedFundingPortfolioOverridePopupProps {
    eventModel: PopulationBasedFundingEventWithNotesModel;
    onClose: () => void;
    onSaveRegistration: (selectedReason: number, customReason: string, note: string, eventId: string) => void;
    onSaveDeregistration: (selectedReason: number, customReason: string, note: string, eventId: string) => void;
}

class PopulationBasedFundingEventWithNotesCollapsibleModel extends PopulationBasedFundingEventWithNotesModel {
    public isCollapsed: boolean;
    constructor(model: PopulationBasedFundingEventWithNotesModel) {
        super();
        Object.assign(this, model);
        this.isCollapsed = true;
    }
}

function PopulationBasedFundingPortfolioOverridePopup(props: PopulationBasedFundingPortfolioOverridePopupProps): JSX.Element {
    const { getText } = useTranslation();
    const [eventToOverride] = useState({ ...props.eventModel });

    const [hasSaveButtonBeenClicked, setHasSaveButtonBeenClicked] = useState<boolean>(false);
    const isServerCallAlreadySent = useRef(false);

    const [patientHistoryItems, setPatientHistoryItems] = useState<PopulationBasedFundingEventWithNotesModel[]>([]);
    const [extendedItems, setExtendedItems] = useState<string[]>([]);

    const availableOverrideReasons = PopulationBasedFundingPortfolioHelper.GetPopulationBasedFundingOverrideReasons(eventToOverride.ActionEnum);
    const [selectedReason, setSelectedReason] = useState();
    const [customReasonLabel, setCustomReasonLabel] = useState<string>("");

    const [note, setNote] = useState<string>("");
    const [isNoteClickableTextShown, toggleClickableNotesVisibility] = useState(false);
    const [isCommentClickableTextShown, toggleClickableCommentVisibility] = useState(false);

    useEffect(
        () => {
            const fetchPatientHistoryData = async (): Promise<void> => {
                const patientHistory = await PopulationBasedFundingPortfolioActions.loadPatientRegistrationHistory(eventToOverride.PatientContactFacetId);
                const collapsiblePatientHistoryItems = patientHistory?.any()
                    ? patientHistory.map(event => new PopulationBasedFundingEventWithNotesCollapsibleModel(event))
                    : [];
                setPatientHistoryItems(collapsiblePatientHistoryItems.filter(p => !p.isPendingProposal));
            };

            fetchPatientHistoryData();
        },
        [eventToOverride.PatientContactFacetId]
    );

    const popupTitle = eventToOverride.ActionEnum == PopulationBasedFundingEventAction.Register
        ? getText("PopulationBasedFunding_RegistrationOverrideLabel")
        : getText("PopulationBasedFunding_DeregistrationOverrideLabel");

    const collapseHistoryItem = (selectedEvent: PopulationBasedFundingEventWithNotesModel): void => {
        const nextExtendedItems = extendedItems.contains(selectedEvent.Id)
            ? extendedItems.filter(id => id !== selectedEvent.Id)
            : [...extendedItems, selectedEvent.Id];

        setExtendedItems(nextExtendedItems);
    };

    const isSelectedReasonAnotherReason = (): boolean => {
        if (eventToOverride.ActionEnum == PopulationBasedFundingEventAction.Register) {
            return selectedReason == PopulationBasedFundingEventOverrideReason.AnotherRegistrationReasonIndicatedInNotes;
        }
        else {
            return selectedReason == PopulationBasedFundingEventOverrideReason.AnotherDeregistrationReasonIndicatedInNotes;
        }
    };

    const isFormValid = (): boolean => !!selectedReason &&
        ((isSelectedReasonAnotherReason() && !!customReasonLabel)
            || (!isSelectedReasonAnotherReason()));

    const callAppropriateSaveMethodBasedOnActionType = (): void => {
        const onSave = async (): Promise<void> => {
            if (eventToOverride.ActionEnum === PopulationBasedFundingEventAction.Register) {
                await props.onSaveRegistration(
                    selectedReason,
                    customReasonLabel,
                    note,
                    eventToOverride.Id
                );
            }
            else {
                await props.onSaveDeregistration(
                    selectedReason,
                    customReasonLabel,
                    note,
                    eventToOverride.Id
                );
            }

            props.onClose();
        };

        if (!isServerCallAlreadySent.current) {
            isServerCallAlreadySent.current = true;
            onSave();
        }

        setHasSaveButtonBeenClicked(true);
    };

    const renderFooter = (): JSX.Element => (
        <div className="footer-button-container">
            <ButtonControl
                className="btn btn-default"
                onClick={() => props.onClose()}
                label={getText("CancelLabel")}
            />
            <ButtonControl
                className="btn btn-primary"
                disabled={hasSaveButtonBeenClicked || !isFormValid()}
                label={getText("SaveLabel")}
                onClick={() => callAppropriateSaveMethodBasedOnActionType()}
            />
        </div>
    );

    return (
        <Modal
            id="PopulationBasedFundingOverridePopup"
            title={popupTitle}
            onClose={() => props.onClose()}
            top={50}
            width={560}
            draggable
            showOverlay={true}
            footer={renderFooter()}
            showX={true}
            disableOverlayClose={true}
            maxHeight="75vh"
        >
            <div className={styles.populationBasedFundingOverridePopup}>
                <div>
                    <PopulationBasedFundingEventDetails
                        physicianName={Employee.getFullNameWithPrefixContactFacet(String(eventToOverride.EmployeeContactFacetId))}
                        effectiveDate={eventToOverride.EffectiveDate}
                        proposalDate={eventToOverride.ProposalDate}
                        overrideDate={eventToOverride.OverrideDate}
                        proposalReasonVocabKey={PopulationBasedFundingHelper.getProposalReasonVocabKey(eventToOverride.ProposalReason)}
                        deregisteredReasonVocabKey={PopulationBasedFundingHelper.getDeregisterReasonVocabKey(eventToOverride.DeregisteredReasonEnum)}
                        overrideReasonVocabKey={PopulationBasedFundingHelper.getOverrideReasonVocabKey(eventToOverride.OverrideReasonEnum)}
                        deregistrationOrProposalCustomReason={eventToOverride.DeregistrationOrProposalCustomReason}
                    />
                </div>

                <div>
                    <h5>{getText("OverrideReasonLabel")}</h5>
                    <SelectLookupControl
                        IncludeEmptyChoice={true}
                        Value={selectedReason}
                        AvailableValues={availableOverrideReasons?.map(reason => new ComboBoxDropDownItem(reason.Enum.toString(), getText(reason.VocabKey)))}
                        Callback={value => setSelectedReason(value)}
                        Item={selectedReason}
                        invalid={!selectedReason}
                    />
                </div>

                {isSelectedReasonAnotherReason() && (
                    <div>
                        <h5>{getText("CommentLabel")}</h5>
                        <TextboxControl
                            id="event-custom-reason"
                            value={customReasonLabel}
                            onChange={(value: string) => setCustomReasonLabel(value)}
                            valid={!!customReasonLabel}
                        />
                        {isCommentClickableTextShown && (
                            <ClickableTextPanelContainer
                                cssClass="text-helper-container"
                                context="PBFNOTES"
                                inputElementId="event-custom-reason"
                                onClose={() => toggleClickableCommentVisibility(false)}
                            />
                        )}
                        <div className="text-right">
                            <a onClick={() => toggleClickableCommentVisibility(true)}> {cacheStore.GetString("SelectFromListLabel")} </a>
                        </div>
                    </div>
                )}

                <div>
                    <h5>{getText("NotesLabel")}</h5>
                    <TextAreaResizable
                        id="override_note_input"
                        verticalResizeOnly={true}
                        isDisabled={false}
                        text={note}
                        maxHeightPx={80}
                        onChange={(value: string) => setNote(value)}
                    />
                    {isNoteClickableTextShown && (
                        <ClickableTextPanelContainer
                            cssClass="text-helper-container"
                            context="PBFNOTES"
                            inputElementId="override_note_input"
                            onClose={() => toggleClickableNotesVisibility(false)}
                            useLocalTextEditor
                        />
                    )}
                    <div className="text-right">
                        <a onClick={() => toggleClickableNotesVisibility(true)}> {cacheStore.GetString("SelectFromListLabel")} </a>
                    </div>
                </div>

                {patientHistoryItems?.any() && (
                    <div className={styles.historyContainer}>
                        <h5>{getText("RegistrationHistoryLabel")}</h5>
                        {patientHistoryItems?.map(item => (
                            <PopulationBasedFundingCollapsibleEventDetails
                                key={String(item.Id)}
                                className={styles.historyItem}
                                eventModel={item}
                                isCollapsed={!extendedItems.includes(item.Id)}
                                onCollapseToggle={() => collapseHistoryItem(item)}
                                disableActions
                            />
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
}

export { PopulationBasedFundingPortfolioOverridePopup };
